import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { executionPlanApi } from '../services/api';
import api from '../services/api';
import DevicePicker from './DevicePicker';
import PremiumBadge from './PremiumBadge';
import { useWorkspace } from '../context/WorkspaceContext';
import './ExecutionPlanWizard.css';

/**
 * ExecutionPlanWizard
 * 
 * Modern stepped wizard for creating execution plans
 * Step 1: Input Requirements (file upload + manual goals)
 * Step 2: Review extracted goals
 * Step 3: Launch configuration
 */
function ExecutionPlanWizard({ onClose, onComplete }) {
    const { currentWorkspace } = useWorkspace();
    const [currentStep, setCurrentStep] = useState(1);
    const [file, setFile] = useState(null);
    const [manualGoals, setManualGoals] = useState('');
    const [extractedGoals, setExtractedGoals] = useState([]);
    const [metadata, setMetadata] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    // Step 3: Configuration
    const [planName, setPlanName] = useState('');
    const [executionMode, setExecutionMode] = useState('sequential');
    const [stopOnFailure, setStopOnFailure] = useState(true);
    const [retryFailedGoals, setRetryFailedGoals] = useState(true);
    const [maxRetries, setMaxRetries] = useState(1);
    const [selectedApp, setSelectedApp] = useState(null);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [showAppPicker, setShowAppPicker] = useState(false);
    const [showDevicePicker, setShowDevicePicker] = useState(false);
    const [apps, setApps] = useState([]);
    const [appsLoading, setAppsLoading] = useState(false);

    const realDevicesEnabled = currentWorkspace?.subscription?.quotas?.realDevicesEnabled === true;

    useEffect(() => {
        if (selectedApp?.platform.toLowerCase() === 'android' && !realDevicesEnabled) {
            setSelectedDevice({
                arn: 'emulator',
                name: 'Android Emulator (Standard)',
                model: 'Virtual Emulator',
                os: 'Android 14 (Standard)',
                platform: 'android',
                availability: 'AVAILABLE',
                provider: 'emulator'
            });
        }
    }, [currentWorkspace, selectedApp, realDevicesEnabled]);

    // Load apps
    const loadApps = useCallback(async () => {
        setAppsLoading(true);
        try {
            const response = await api.get('/apps', { params: { limit: 50 } });
            setApps(response.data.rows || []);
        } catch (err) {
            console.error('Failed to load apps:', err);
        } finally {
            setAppsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadApps();
    }, [loadApps]);

    // Step 1: File upload and manual input
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            validateAndSetFile(droppedFile);
        }
    }, []);

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            validateAndSetFile(selectedFile);
        }
    };

    const validateAndSetFile = (selectedFile) => {
        const validTypes = [
            'application/pdf',
            'text/markdown',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'text/plain'
        ];

        const validExtensions = ['.pdf', '.md', '.markdown', '.docx', '.txt'];
        const fileName = selectedFile.name.toLowerCase();
        const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

        if (!validTypes.includes(selectedFile.type) && !hasValidExtension) {
            setError('Invalid file type. Please upload PDF, Markdown, DOCX, or TXT files.');
            return;
        }

        setFile(selectedFile);
        setError(null);
    };

    const handleExtractGoals = async () => {
        if (!file && !manualGoals.trim()) {
            setError('Please upload a document or enter goals manually.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let goals = [];
            let meta = null;

            // If file is uploaded, parse it
            if (file) {
                const response = await executionPlanApi.parseDocument(file, manualGoals);
                goals = response.data.goals || [];
                meta = response.data.metadata;
                setPlanName(`Test Plan - ${file.name}`);
            }

            // If manual goals are provided (and no file), parse them
            if (!file && manualGoals.trim()) {
                const response = await executionPlanApi.parseChatMessage(manualGoals);
                goals = response.data.goals || [];
                meta = response.data.metadata;
                setPlanName(`Test Plan - ${new Date().toLocaleDateString()}`);
            }

            setExtractedGoals(goals);
            setMetadata(meta);
            setCurrentStep(2);
        } catch (err) {
            console.error('Failed to extract goals:', err);
            setError('Failed to extract goals. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Goal management
    const toggleGoalEnabled = (index) => {
        const updated = [...extractedGoals];
        updated[index].isEnabled = !updated[index].isEnabled;
        setExtractedGoals(updated);
    };

    const updateGoalTitle = (index, title) => {
        const updated = [...extractedGoals];
        updated[index].title = title;
        setExtractedGoals(updated);
    };

    const updateGoalDescription = (index, description) => {
        const updated = [...extractedGoals];
        updated[index].description = description;
        setExtractedGoals(updated);
    };

    const moveGoal = (index, direction) => {
        const updated = [...extractedGoals];
        const newIndex = direction === 'up' ? index - 1 : index + 1;

        if (newIndex < 0 || newIndex >= updated.length) return;

        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        updated.forEach((goal, i) => {
            goal.order = i + 1;
        });

        setExtractedGoals(updated);
    };

    const removeGoal = (index) => {
        const updated = extractedGoals.filter((_, i) => i !== index);
        updated.forEach((goal, i) => {
            goal.order = i + 1;
        });
        setExtractedGoals(updated);
    };

    // Step 3: App and device selection
    const handleAppSelect = (app) => {
        setSelectedApp(app);
        const appPlatform = app.platform.toLowerCase();
        if (appPlatform === 'android' && !realDevicesEnabled) {
            setSelectedDevice({
                arn: 'emulator',
                name: 'Android Emulator (Standard)',
                model: 'Virtual Emulator',
                os: 'Android 14 (Standard)',
                platform: 'android',
                availability: 'AVAILABLE',
                provider: 'emulator'
            });
        } else if (selectedDevice && selectedDevice.platform !== appPlatform) {
            setSelectedDevice(null);
        }
        setShowAppPicker(false);
    };

    const handleDeviceSelect = (device) => {
        setSelectedDevice(device);
        setShowDevicePicker(false);
    };

    const handleCreatePlan = async () => {
        if (!selectedApp) {
            setError('Please select an app to test');
            return;
        }
        if (!selectedDevice) {
            setError('Please select a device');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await executionPlanApi.createExecutionPlan({
                name: planName,
                goals: extractedGoals.filter(g => g.isEnabled !== false),
                platform: metadata?.suggestedPlatform || selectedApp.platform,
                sourceType: file ? 'document+chat' : 'chat',
                sourceMessage: manualGoals,
                sourceDocument: file?.name,
                executionMode,
                stopOnFailure,
                retryFailedGoals,
                maxRetries,
                appId: selectedApp.id,
                deviceProvider: selectedDevice.provider || 'aws',
                deviceArn: selectedDevice.arn
            });

            if (onComplete) {
                onComplete(response.data.executionPlan);
            } else {
                onClose();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create execution plan');
        } finally {
            setLoading(false);
        }
    };

    const enabledGoalsCount = extractedGoals.filter(g => g.isEnabled !== false).length;

    return (
        <div className="execution-plan-wizard">
            <div className="execution-plan-wizard__overlay" onClick={onClose} />

            <div className="execution-plan-wizard__container">
                {/* Close button */}
                <button className="execution-plan-wizard__close" onClick={onClose}>
                    ✕
                </button>

                {/* Step indicators */}
                <div className="execution-plan-wizard__steps">
                    <div className={`execution-plan-wizard__step ${currentStep >= 1 ? 'active' : ''}`}>
                        <span className="execution-plan-wizard__step-number">1</span>
                        <span className="execution-plan-wizard__step-label">Input Requirements</span>
                    </div>
                    <div className={`execution-plan-wizard__step ${currentStep >= 2 ? 'active' : ''}`}>
                        <span className="execution-plan-wizard__step-number">2</span>
                        <span className="execution-plan-wizard__step-label">Review</span>
                    </div>
                    <div className={`execution-plan-wizard__step ${currentStep >= 3 ? 'active' : ''}`}>
                        <span className="execution-plan-wizard__step-number">3</span>
                        <span className="execution-plan-wizard__step-label">Launch</span>
                    </div>
                </div>

                {/* Step 1: Input Requirements */}
                {currentStep === 1 && (
                    <div className="execution-plan-wizard__content">
                        <h1 className="execution-plan-wizard__title">Let's build your plan.</h1>
                        <p className="execution-plan-wizard__subtitle">
                            Upload your product documentation or PRD, and we'll extract the core testing objectives.
                        </p>

                        {/* File drop zone */}
                        <div
                            className={`execution-plan-wizard__dropzone ${isDragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            <div className="execution-plan-wizard__dropzone-icon">
                                📄
                            </div>
                            <p className="execution-plan-wizard__dropzone-text">
                                {file ? file.name : 'Drop your specification here'}
                            </p>
                            <p className="execution-plan-wizard__dropzone-hint">
                                Accepts PDF, Markdown, or DOCX
                            </p>
                            <input
                                type="file"
                                id="file-input"
                                className="execution-plan-wizard__file-input"
                                accept=".pdf,.md,.docx,.txt"
                                onChange={handleFileSelect}
                            />
                            <label htmlFor="file-input" className="execution-plan-wizard__browse-btn">
                                Browse Files
                            </label>
                        </div>

                        {/* Manual goals input */}
                        <div className="execution-plan-wizard__manual-input">
                            <div className="execution-plan-wizard__manual-header">
                                <label>Or Enter Goals Directly</label>
                                <span className="execution-plan-wizard__optional">OPTIONAL</span>
                            </div>
                            <textarea
                                className="execution-plan-wizard__textarea"
                                placeholder="You can also type your testing goals here instead of uploading a document, or provide additional context to supplement your upload..."
                                value={manualGoals}
                                onChange={(e) => setManualGoals(e.target.value)}
                                rows={4}
                            />
                        </div>

                        {error && (
                            <div className="execution-plan-wizard__error">
                                {error}
                            </div>
                        )}

                        {/* Footer */}
                        <div className="execution-plan-wizard__footer">
                            <div className="execution-plan-wizard__progress">
                                STEP 1 OF 3
                            </div>
                            <button
                                className="execution-plan-wizard__cta"
                                onClick={handleExtractGoals}
                                disabled={loading || (!file && !manualGoals.trim())}
                            >
                                {loading ? 'Extracting...' : 'Extract Goals'} →
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Review Goals */}
                {currentStep === 2 && (
                    <div className="execution-plan-wizard__content execution-plan-wizard__content--review">
                        <h1 className="execution-plan-wizard__title">Review your goals.</h1>
                        <p className="execution-plan-wizard__subtitle">
                            We extracted {extractedGoals.length} testing goals. Edit, reorder, or disable as needed.
                        </p>

                        <div className="execution-plan-wizard__goals-list">
                            {extractedGoals.map((goal, index) => (
                                <div key={index} className={`execution-plan-wizard__goal ${goal.isEnabled === false ? 'disabled' : ''}`}>
                                    <div className="execution-plan-wizard__goal-order">{goal.order}</div>

                                    <div className="execution-plan-wizard__goal-content">
                                        {/* <input
                                            type="text"
                                            value={goal.title}
                                            onChange={(e) => updateGoalTitle(index, e.target.value)}
                                            className="execution-plan-wizard__goal-title"
                                        /> */}
                                        <textarea
                                            value={goal.description}
                                            onChange={(e) => updateGoalDescription(index, e.target.value)}
                                            className="execution-plan-wizard__goal-description"
                                            rows={2}
                                        />
                                        <div className="execution-plan-wizard__goal-meta">
                                            <span className={`execution-plan-wizard__priority ${goal.priority}`}>
                                                {goal.priority}
                                            </span>
                                            <span className="execution-plan-wizard__duration">
                                                ~{goal.estimatedDuration}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="execution-plan-wizard__goal-actions">
                                        <button onClick={() => moveGoal(index, 'up')} disabled={index === 0} title='Move Up'>↑</button>
                                        <button onClick={() => moveGoal(index, 'down')} disabled={index === extractedGoals.length - 1} title='Move Down'>↓</button>
                                        {/* <button onClick={() => toggleGoalEnabled(index)} title={goal.isEnabled ? 'Disable' : 'Enable'}>
                                            {goal.isEnabled === false || goal.isEnabled === undefined ? '👁️' : '✓'}
                                        </button> */}
                                        <button onClick={() => removeGoal(index)} className="danger" title="Remove Goal">🗑️</button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {error && (
                            <div className="execution-plan-wizard__error">
                                {error}
                            </div>
                        )}

                        <div className="execution-plan-wizard__footer">
                            <button className="execution-plan-wizard__back" onClick={() => setCurrentStep(1)}>
                                ← Back
                            </button>
                            <div className="execution-plan-wizard__footer-right">
                                <div className="execution-plan-wizard__progress">
                                    STEP 2 OF 3
                                </div>
                                <button
                                    className="execution-plan-wizard__cta"
                                    onClick={() => setCurrentStep(3)}
                                    disabled={enabledGoalsCount === 0}
                                >
                                    Configure →
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3: Launch Configuration */}
                {currentStep === 3 && (
                    <div className="execution-plan-wizard__content execution-plan-wizard__content--launch">
                        <h1 className="execution-plan-wizard__title">Ready to launch.</h1>
                        <p className="execution-plan-wizard__subtitle">
                            Configure your execution settings and select the app and device.
                        </p>

                        <div className="execution-plan-wizard__config-form">
                            <div className="execution-plan-wizard__form-group">
                                <label>Plan Name</label>
                                <input
                                    type="text"
                                    value={planName}
                                    onChange={(e) => setPlanName(e.target.value)}
                                    placeholder="My Test Plan"
                                />
                            </div>

                            <div className="execution-plan-wizard__form-group">
                                <label>App Selection *</label>
                                <button
                                    type="button"
                                    className="execution-plan-wizard__selector-btn"
                                    onClick={() => setShowAppPicker(true)}
                                >
                                    {selectedApp ? (
                                        <span>{selectedApp.displayName || selectedApp.name}</span>
                                    ) : (
                                        <span className="placeholder">Select app to test</span>
                                    )}
                                </button>
                                {selectedApp && (
                                    <div className="execution-plan-wizard__selected-info">
                                        <span className="platform-badge">{selectedApp.platform}</span>
                                        {selectedApp.version && <span>v{selectedApp.version}</span>}
                                    </div>
                                )}
                            </div>

                            <div className="execution-plan-wizard__form-group">
                                <label>Device Selection *</label>
                                <button
                                    type="button"
                                    className="execution-plan-wizard__selector-btn"
                                    onClick={() => {
                                        if (!selectedApp) return;
                                        if (selectedApp.platform.toLowerCase() === 'android' && !realDevicesEnabled) return;
                                        setShowDevicePicker(true);
                                    }}
                                    disabled={!selectedApp || (selectedApp.platform.toLowerCase() === 'android' && !realDevicesEnabled)}
                                    title={!selectedApp ? 'Select an app first' : (selectedApp.platform.toLowerCase() === 'android' && !realDevicesEnabled) ? 'Standard emulator selected. Upgrade to access real devices.' : ''}
                                >
                                    {selectedDevice ? (
                                        <span>{selectedDevice.name}</span>
                                    ) : (
                                        <span className="placeholder">{selectedApp ? 'Select device' : 'Select app first'}</span>
                                    )}
                                    {!realDevicesEnabled && <PremiumBadge title='Upgrade to run tests on real devices' />}
                                </button>
                                {selectedDevice && (
                                    <div className="execution-plan-wizard__selected-info">
                                        <span className="platform-badge">{selectedDevice.platform}</span>
                                        <span>{selectedDevice.os}</span>
                                    </div>
                                )}
                                {!realDevicesEnabled && selectedApp && selectedApp.platform.toLowerCase() === 'android' && (
                                    <div className="emulator-warning-info" style={{ marginTop: '5px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                        ℹ️ Your tests will run on Android Emulator. <Link to="/settings/billing" style={{ color: 'var(--primary-color)', textDecoration: 'underline', fontWeight: 'bold' }}>Upgrade to Business or Enterprise</Link> to run tests on real AWS devices.
                                    </div>
                                )}
                            </div>

                            <div className="execution-plan-wizard__form-group">
                                <label>Execution Mode</label>
                                <select value={executionMode} onChange={(e) => setExecutionMode(e.target.value)}>
                                    <option value="sequential">Sequential (one after another)</option>
                                    <option value="parallel">Parallel (experimental)</option>
                                </select>
                            </div>

                            <div className="execution-plan-wizard__form-group checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={stopOnFailure}
                                        onChange={(e) => setStopOnFailure(e.target.checked)}
                                    />
                                    Stop execution if a goal fails
                                </label>
                            </div>

                            <div className="execution-plan-wizard__form-group checkbox">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={retryFailedGoals}
                                        onChange={(e) => setRetryFailedGoals(e.target.checked)}
                                    />
                                    Retry failed goals
                                </label>
                            </div>

                            {retryFailedGoals && (
                                <div className="execution-plan-wizard__form-group">
                                    <label>Max Retries</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="5"
                                        value={maxRetries}
                                        onChange={(e) => setMaxRetries(parseInt(e.target.value))}
                                    />
                                </div>
                            )}

                            <div className="execution-plan-wizard__summary">
                                <h4>Summary</h4>
                                <p><strong>{enabledGoalsCount}</strong> goals will be executed</p>
                                <p>Mode: <strong>{executionMode}</strong></p>
                                <p>App: <strong>{selectedApp ? (selectedApp.displayName || selectedApp.name) : 'Not selected'}</strong></p>
                                <p>Device: <strong>{selectedDevice ? selectedDevice.name : 'Not selected'}</strong></p>
                            </div>
                        </div>

                        {error && (
                            <div className="execution-plan-wizard__error">
                                {error}
                            </div>
                        )}

                        <div className="execution-plan-wizard__footer">
                            <button className="execution-plan-wizard__back" onClick={() => setCurrentStep(2)}>
                                ← Back
                            </button>
                            <div className="execution-plan-wizard__footer-right">
                                <div className="execution-plan-wizard__progress">
                                    STEP 3 OF 3
                                </div>
                                <button
                                    className="execution-plan-wizard__cta"
                                    onClick={handleCreatePlan}
                                    disabled={!planName.trim() || !selectedApp || !selectedDevice || loading}
                                >
                                    {loading ? 'Creating...' : 'Create Plan'} →
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* App Picker Modal */}
                {showAppPicker && (
                    <div className="execution-plan-wizard__modal-overlay" onClick={() => setShowAppPicker(false)}>
                        <div className="execution-plan-wizard__modal-content" onClick={e => e.stopPropagation()}>
                            <div className="execution-plan-wizard__modal-header">
                                <h3>Select App</h3>
                                <button className="execution-plan-wizard__close" onClick={() => setShowAppPicker(false)}>×</button>
                            </div>
                            <div className="execution-plan-wizard__app-picker">
                                {appsLoading ? (
                                    <div className="loading">Loading apps...</div>
                                ) : apps.length === 0 ? (
                                    <div className="empty-state">
                                        <p>No apps uploaded yet</p>
                                    </div>
                                ) : (
                                    <div className="execution-plan-wizard__app-list">
                                        {apps.map(app => (
                                            <div
                                                key={app.id}
                                                className={`execution-plan-wizard__app-item ${selectedApp?.id === app.id ? 'selected' : ''}`}
                                                onClick={() => handleAppSelect(app)}
                                            >
                                                <div className="execution-plan-wizard__app-name">{app.displayName || app.name}</div>
                                                <div className="execution-plan-wizard__app-details">
                                                    <span className="platform-badge">{app.platform}</span>
                                                    {app.version && <span>v{app.version}</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Device Picker Modal */}
                {showDevicePicker && selectedApp && (
                    <div className="execution-plan-wizard__modal-overlay" onClick={() => setShowDevicePicker(false)}>
                        <div className="execution-plan-wizard__modal-content" onClick={e => e.stopPropagation()}>
                            <div className="execution-plan-wizard__modal-header">
                                <h3>Select Device <PremiumBadge title='Upgrade to run tests on real devices' /></h3>
                                <button className="execution-plan-wizard__close" onClick={() => setShowDevicePicker(false)}>×</button>
                            </div>
                            <DevicePicker
                                platform={selectedApp.platform.toUpperCase()}
                                onDeviceSelect={handleDeviceSelect}
                                selectedDeviceArn={selectedDevice?.arn}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ExecutionPlanWizard;
